<script setup>
import { computed, inject } from 'vue';
import TrendLineChart from '@/components/result/MLSRD-2026/TrendLineChart.vue';


// 부모에서 주입받은 데이터
const studentData = inject('studentData');


// 차트 데이터가 2개 이상 있는지 확인
const hasMultipleChartData = computed(() => {
    if (!studentData.value || !studentData.value.beforeScores) return false;
    
    const beforeScores = studentData.value.beforeScores;
    const currentData = studentData.value;
    
    // 이전 데이터 개수 + 현재 데이터(1개) >= 2
    return beforeScores.length + 1 >= 2;
});

// 차트 카테고리 (학년별)
const categories = ['초4', '초6', '중2', '고1'];

// 문해력 차트 데이터
const literacyChartData = computed(() => {
    if (!studentData.value) return [];
    
    const beforeScores = studentData.value.beforeScores || [];
    const currentData = studentData.value;
    
    // 모든 점수 데이터 (이전 + 현재) 수집
    const allScores = [...beforeScores];
    if (currentData.korscore) {
        allScores.push({
            grade_code: currentData.grade_code,
            korscore: currentData.korscore,
            exam_date: currentData.exam_date || new Date().toISOString()
        });
    }
    
    // 학년별로 그룹화하고 최신 점수만 유지
    const gradeScores = {};
    allScores.forEach(score => {
        if (score.korscore) {
            const grade = score.grade_code;
            if (!gradeScores[grade] || new Date(score.exam_date) > new Date(gradeScores[grade].exam_date)) {
                gradeScores[grade] = score;
            }
        }
    });
    
    // 차트 데이터 형식으로 변환
    const scores = categories.map(category => {
        const gradeKey = category === '초4' ? '4' : category === '초6' ? '6' : category === '중2' ? '2' : '1';
        return gradeScores[gradeKey]?.korscore || null;
    });
    
    return [{
        label: '문해력',
        scores: scores
    }];
});

// 수리력 차트 데이터
const numeracyChartData = computed(() => {
    if (!studentData.value) return [];
    
    const beforeScores = studentData.value.beforeScores || [];
    const currentData = studentData.value;
    
    // 모든 점수 데이터 (이전 + 현재) 수집
    const allScores = [...beforeScores];
    if (currentData.matscore) {
        allScores.push({
            grade_code: currentData.grade_code,
            matscore: currentData.matscore,
            exam_date: currentData.exam_date || new Date().toISOString()
        });
    }
    
    // 학년별로 그룹화하고 최신 점수만 유지
    const gradeScores = {};
    allScores.forEach(score => {
        if (score.matscore) {
            const grade = score.grade_code;
            if (!gradeScores[grade] || new Date(score.exam_date) > new Date(gradeScores[grade].exam_date)) {
                gradeScores[grade] = score;
            }
        }
    });
    
    // 차트 데이터 형식으로 변환
    const scores = categories.map(category => {
        const gradeKey = category === '초4' ? '4' : category === '초6' ? '6' : category === '중2' ? '2' : '1';
        return gradeScores[gradeKey]?.matscore || null;
    });
    
    return [{
        label: '수리력',
        scores: scores
    }];
});

</script>
<template>
  <section class="student-growth">
    <h5>문해력·수리력 성장 그래프</h5>
    <p class="description">학생이 학년이 바뀌어 다시 이 검사를 치르게 된다면, 이전 결과와 비교하여 학생이 어느 정도 성장했는지 알려주는 그래프입니다.</p>
    <p class="growth-note">{{ hasMultipleChartData ? '2023년 점수는 2024년 기준척도로 변환된 점수입니다.' : '2024년 이후 응시한 진단검사 결과 데이터가 반영됩니다.' }}</p>
    <div class="growth-charts">
      <TrendLineChart :categories="categories" :data="literacyChartData" chartType="literacy" chartHeight="160px" />
      <TrendLineChart :categories="categories" :data="numeracyChartData" chartType="numeracy" chartHeight="160px" />
    </div>

    <aside>
      <p class="score-description"><strong class="literacy-light-color">서울 학생 문해력·수리력 진단검사 점수</strong>는 초등학교 4학년부터 고등학교 1학년까지의 문해력·수리력 점수 범위를 1,000~2,000점이 되게 개발한 것입니다. 평균적으로 점수는 저학년일수록 낮고, 고학년일수록 높습니다. 학년별 점수 범위는 다음과 같습니다.</p>
      <!-- <div class="score-tables">
        <table v-for="subject in ['literacy', 'numeracy']" :key="subject" :class="subject" :aria-label="subject === 'literacy' ? '문해력 학년별 점수 범위' : '수리력 학년별 점수 범위'">
          <thead><tr><th>초등학교 4학년</th><th>초등학교 6학년</th><th>중학교 2학년</th><th>고등학교 1학년</th></tr></thead>
          <tbody><tr><td>1000점~1700점</td><td>1050점~1800점</td><td>1100점~1900점</td><td>1150점~2000점</td></tr></tbody>
        </table>
      </div> -->
    </aside>
  </section>
</template>
<style scoped>
.student-growth { margin-top: 10px; padding: 14px 18px; border-radius: 12px; background: #fafafa; }
h5 { font-size: 22px; font-weight: 600; }
.description { font-size: 16px; line-height: 1.4; margin-top: 4px; }
.growth-charts { display: flex; gap: 20px; margin-top: 8px; }
.growth-charts > * { flex: 1; min-width: 0; }
.growth-note { font-size: 14px; line-height: 1.4; }
.score-description { font-size: 16px; line-height: 1.3; margin: 8px 0 6px; }
.score-tables { display: flex; gap: 20px; }
.score-tables table { flex: 1; width: 50%; table-layout: fixed; font-size: 13px; border-collapse: collapse; }
.score-tables th, .score-tables td { padding: 3px 0; text-align: center; border: 1px solid #ccc; }
.score-tables th { color: white; font-weight: 500; }
.score-tables .literacy th { background: var(--result-light-blue); }
.score-tables .numeracy th { background: var(--result-light-red); }
</style>
